// src/device/ids.ts
// THE IDEMPOTENCY KEYS. This file is the reason a replayed queue writes nothing new.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE CONTRACT, from the backend
// ══════════════════════════════════════════════════════════════════════════════════════════════
//   · `(client_device_id, client_session_uuid)` is what makes a re-posted session THE SAME session —
//     src/practice/routes/sessions.ts returns `replayed: true` and one row, not two.
//   · `client_upload_uuid` is what makes a re-initiated upload THE SAME upload — the phone that died
//     after part 2 of 3 comes back and is asked for part 3 only.
//
// Both are generated ON THE DEVICE, OFFLINE, BEFORE anything is sent. That ordering is the whole
// point: a key minted by the server cannot exist for a session created in a basement with no signal.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE MISTAKE THIS FILE EXISTS TO PREVENT
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Regenerating a key on retry. A `crypto.randomUUID()` at the top of a retry function turns every
// retry into a NEW session and a NEW upload — the server dutifully creates a second row for each,
// because from its side that is a different key and it has no way to know better. The idempotency
// is then perfect and useless: the client defeated it.
//
// So: `client_device_id` is minted ONCE and persisted in IndexedDB, and a session's
// `client_session_uuid` is minted ONCE when the job is enqueued and stored WITH the job. Neither is
// ever recomputed. Nothing in this module has a "refresh" path, and the queue never asks for one.

import { STORE, idbGet, idbPut } from '../store/idb';

const DEVICE_KEY = 'device_id';

/**
 * `crypto.randomUUID` needs a secure context. On plain-http LAN testing (a phone hitting a laptop by
 * IP) it is undefined, and an app that throws there is an app that cannot be tested at a ground.
 * `getRandomValues` is available in far more places; the last resort is only for a node test runner.
 */
function uuidv4(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = ((b[6] as number) & 0x0f) | 0x40;
  b[8] = ((b[8] as number) & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** A fresh session idempotency key. Called ONCE per session, at enqueue time, and then stored. */
export function newSessionUuid(): string {
  return uuidv4();
}

/**
 * A fresh upload idempotency key.
 *
 * Must satisfy the backend's `CLIENT_UUID_RE` = /^[A-Za-z0-9_.:-]{8,128}$/ — a plain uuid v4 does,
 * and this is prefixed for legibility in logs while staying inside the character class.
 */
export function newUploadUuid(): string {
  return `up-${uuidv4()}`;
}

let cached: string | null = null;

/**
 * THE device id. Minted once, then read forever.
 *
 * Not `navigator.userAgent`, not a fingerprint: those change under a browser update and would
 * silently split one device's queue into two. A random id persisted in IndexedDB survives a reload,
 * a force-quit and an OS restart, and changes only if the user clears site data — at which point
 * the queue it keyed is gone too, so the two stay consistent.
 */
export async function deviceId(): Promise<string> {
  if (cached) return cached;
  const existing = await idbGet<string>(STORE.kv, DEVICE_KEY);
  if (typeof existing === 'string' && existing) {
    cached = existing;
    return existing;
  }
  const minted = `dev-${uuidv4()}`;
  await idbPut(STORE.kv, minted, DEVICE_KEY);
  cached = minted;
  return minted;
}

/** Test seam only. There is no product path that changes a device id. */
export function __resetDeviceIdCache(): void {
  cached = null;
}
